import { DEFAULT_PAGE_SIZE } from "@design-system/pagination/schemas";
import { bindClient } from "@spiriidk/utils";
import {
  infiniteQueryOptions,
  skipToken,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { getCompaniesPage } from "connect-api/companies/api/get-companies";
import type {
  CompaniesSearch,
  Company,
} from "connect-api/companies/schemas/company";
import { useEffect } from "react";

import { typedFetchNoImpersonate } from "~/api/typed-fetch";

const companiesApi = bindClient({ getCompaniesPage }, typedFetchNoImpersonate);

const MIN_OFFERED_COMPANIES = DEFAULT_PAGE_SIZE / 2;

type CompaniesQueryParams = Omit<CompaniesSearch, "offset" | "limit">;
interface NamedCompany extends Company {
  name: string;
}

function withoutRepeatedIds(companies: Company[]): Company[] {
  const seenIds = new Set<number>();

  return companies.filter((company) => {
    if (seenIds.has(company.id)) return false;
    seenIds.add(company.id);
    return true;
  });
}

function hasName(company: Company): company is NamedCompany {
  return typeof company.name === "string" && company.name.trim() !== "";
}

const companiesQueryOptions = {
  list: (params: CompaniesQueryParams | typeof skipToken) =>
    infiniteQueryOptions({
      queryKey: ["companies", "infinite", params] as const,
      queryFn:
        params === skipToken
          ? skipToken
          : ({ signal, pageParam }) =>
              companiesApi.getCompaniesPage(
                {
                  ...params,
                  limit: DEFAULT_PAGE_SIZE,
                  offset: pageParam * DEFAULT_PAGE_SIZE,
                },
                signal
              ),
      initialPageParam: 0,
      getNextPageParam: (lastPage, _allPages, lastPageParam) =>
        lastPage.meta.page < lastPage.meta.totalPages
          ? lastPageParam + 1
          : undefined,
    }),
};

function useCompanies(params: CompaniesQueryParams | typeof skipToken) {
  const query = useInfiniteQuery(companiesQueryOptions.list(params));
  const { fetchNextPage } = query;

  const companies = withoutRepeatedIds(
    query.data?.pages.flatMap((page) => page.data) ?? []
  ).filter(hasName);

  const isTooShortToScroll =
    query.hasNextPage &&
    !query.isFetchingNextPage &&
    !query.isFetchNextPageError &&
    companies.length < MIN_OFFERED_COMPANIES;

  useEffect(() => {
    if (isTooShortToScroll) void fetchNextPage();
  }, [isTooShortToScroll, fetchNextPage]);

  return {
    companies,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage,
  };
}

export { companiesQueryOptions, useCompanies };
export type { CompaniesQueryParams };
